import { IconButton } from '../IconButton';
import React, { PropsWithChildren, cloneElement, isValidElement } from 'react';
import { Picker, type SelectedEmoji } from './Picker';
import { Icon } from '../Icon';

export type { SelectedEmoji };

type Props = PropsWithChildren<{
  onEmojiSelect: (emoji: SelectedEmoji) => void;
  onClickOutside?: () => void;
  className?: string;
  position?: 'top' | 'bottom';
  modal?: boolean;
}>;

const positions = {
  top: 'bottom-[100%] mb-1',
  bottom: 'top-[100%] mt-1',
};

export function EmojiPicker({
  onEmojiSelect,
  onClickOutside = () => {},
  className = '',
  position = 'top',
  modal = false,
  children,
}: Props) {
  const [showEmojiPicker, setShowEmojiPicker] = React.useState<boolean>(false);

  const handleToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowEmojiPicker(!showEmojiPicker);
  };

  const handleClose = () => {
    setShowEmojiPicker(false);
    onClickOutside();
  };

  const handleEmojiSelect = (emoji: SelectedEmoji) => {
    onEmojiSelect(emoji);
    setShowEmojiPicker(false);
  };

  if (modal) {
    return (
      <>
        <div className={className}>
          {children ? (
            isValidElement(children) ? (
              cloneElement(children as React.ReactElement<any>, {
                onClick: (e: React.MouseEvent) => {
                  handleToggle(e);
                  if ((children as React.ReactElement<any>).props.onClick) {
                    (children as React.ReactElement<any>).props.onClick(e);
                  }
                },
              })
            ) : (
              children
            )
          ) : (
            <IconButton
              icon="sticker"
              onClick={handleToggle}
              className="text-blue-600"
              transparent
            />
          )}
        </div>
        {showEmojiPicker && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
            onClick={handleClose}
          >
            <div
              className="bg-white rounded-lg shadow-xl w-auto mx-4 max-w-[calc(100vw-2rem)] overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Title Bar */}
              <div className="flex items-center justify-between px-4 py-3 border-b border-neutral-200">
                <h2 className="text-lg font-medium text-neutral-900">Emoji</h2>
                <button
                  onClick={handleClose}
                  className="text-neutral-400 hover:text-neutral-600 transition-colors"
                >
                  <Icon icon="close" width={20} height={20} />
                </button>
              </div>
              
              {/* Picker Content */}
              <div className="p-0">
                <Picker
                  onEmojiSelect={handleEmojiSelect}
                  onClickOutside={handleClose}
                />
              </div>
            </div>
          </div>
        )}
      </>
    );
  }

  return (
    <div className={`relative flex align-center ${className}`}>
      {children ? (
        isValidElement(children) ? (
          cloneElement(children as React.ReactElement<any>, {
            onClick: (e: React.MouseEvent) => {
              handleToggle(e);
              if ((children as React.ReactElement<any>).props.onClick) {
                (children as React.ReactElement<any>).props.onClick(e);
              }
            },
          })
        ) : (
          children
        )
      ) : (
        <IconButton
          icon="sticker"
          onClick={handleToggle}
          className="text-blue-600"
          transparent
        />
      )}
      {showEmojiPicker && (
        <div
          className={`absolute ${positions[position]} z-50 max-w-[min(352px,calc(100vw-6rem))] overflow-hidden rounded-lg`}
        >
          <Picker
            onEmojiSelect={handleEmojiSelect}
            onClickOutside={handleClose}
          />
        </div>
      )}
    </div>
  );
}
